import React, { useEffect, useState, useMemo } from "react";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, FileText, Upload, Download, Trash2 } from "lucide-react";
import { downloadCsv } from "@/lib/download";
import { toast } from "sonner";

const statusColors = {
  Received: "bg-blue-50 text-blue-700 border-blue-200",
  Acknowledged: "bg-amber-50 text-amber-700 border-amber-200",
  "In Production": "bg-violet-50 text-violet-700 border-violet-200",
  Dispatched: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export default function POList() {
  const [pos, setPos] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  const load = () => {
    setLoading(true);
    api.get("/po").then((r) => setPos(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = (visibleIds) => {
    if (visibleIds.every((id) => selected.has(id))) {
      const s = new Set(selected);
      visibleIds.forEach((id) => s.delete(id));
      setSelected(s);
    } else {
      setSelected(new Set([...selected, ...visibleIds]));
    }
  };
  const removeOne = async (id, po_number) => {
    if (!window.confirm(`Delete PO ${po_number || id}?`)) return;
    try {
      await api.delete(`/po/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Failed"); }
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete ${selected.size} PO${selected.size > 1 ? "s" : ""}?`)) return;
    try {
      const res = await api.post("/po/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${res.data.deleted}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error("Bulk delete failed"); }
  };
  const deleteAll = async () => {
    if (!window.confirm(`Delete ALL ${pos.length} POs? This cannot be undone.`)) return;
    try {
      const res = await api.post("/po/bulk-delete", { all: true });
      toast.success(`Deleted ${res.data.deleted}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const filtered = useMemo(() => {
    return pos.filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (!q.trim()) return true;
      const t = q.toLowerCase();
      return (
        (p.po_number || "").toLowerCase().includes(t) ||
        (p.customer_name || "").toLowerCase().includes(t) ||
        (p.file_name || "").toLowerCase().includes(t)
      );
    });
  }, [pos, q, statusFilter]);

  return (
    <Layout
      title="Purchase Orders"
      subtitle="All decoded POs, newest first"
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => downloadCsv("/export/po", "purchase_orders.csv")} data-testid="export-pos-btn" className="text-xs text-slate-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 transition-colors inline-flex items-center gap-2">
            <Download size={14} /> Export CSV
          </button>
          <Link to="/upload" data-testid="list-upload-cta" className="bg-slate-900 text-white hover:bg-slate-800 rounded-md px-4 py-2 text-sm font-medium transition-colors inline-flex items-center gap-2">
            <Upload size={14} /> Upload
          </Link>
        </div>
      }
    >
      <div className="bg-white border border-gray-200 rounded-md">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-gray-100">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <input
              data-testid="po-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search PO#, customer, filename..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:ring-2 focus:ring-slate-300 focus:outline-none focus:border-slate-400"
            />
          </div>
          <select
            data-testid="po-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-md px-3 py-2 focus:ring-2 focus:ring-slate-300 focus:outline-none focus:border-slate-400 bg-white"
          >
            <option value="all">All statuses</option>
            <option value="Received">Received</option>
            <option value="Acknowledged">Acknowledged</option>
            <option value="In Production">In Production</option>
            <option value="Dispatched">Dispatched</option>
          </select>
        </div>

        {loading ? (
          <div className="p-12 text-center text-sm text-gray-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center" data-testid="po-empty-state">
            <FileText className="mx-auto text-gray-300 mb-3" size={32} />
            <div className="font-display text-lg font-semibold text-slate-900">No purchase orders yet</div>
            <div className="text-sm text-gray-500 mt-1">Upload your first PO to get started.</div>
            <Link to="/upload" className="mt-4 inline-flex items-center gap-2 bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-800 transition-colors">
              <Upload size={14} /> Upload PO
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-100">
              <div className="text-xs text-gray-600" data-testid="po-selection-count">
                {selected.size > 0 ? `${selected.size} selected` : `${filtered.length} POs`}
              </div>
              <div className="flex items-center gap-2">
                {selected.size > 0 && (
                  <button onClick={bulkDelete} data-testid="bulk-delete-pos-btn" className="text-xs text-red-600 border border-red-200 rounded-md px-2.5 py-1 hover:bg-red-50 inline-flex items-center gap-1">
                    <Trash2 size={12} /> Delete selected ({selected.size})
                  </button>
                )}
                <button onClick={deleteAll} data-testid="delete-all-pos-btn" className="text-xs text-red-600 border border-red-200 rounded-md px-2.5 py-1 hover:bg-red-50 inline-flex items-center gap-1">
                  <Trash2 size={12} /> Delete all
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="po-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="w-10 px-4 py-3">
                      <input type="checkbox" checked={filtered.length > 0 && filtered.every((p) => selected.has(p.id))} onChange={() => toggleAll(filtered.map((p) => p.id))} data-testid="po-select-all" className="cursor-pointer" />
                    </th>
                    <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">PO Number</th>
                    <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">Customer</th>
                    <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">PO Date</th>
                    <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">Due Date</th>
                    <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-right">Items</th>
                    <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-right">Value</th>
                    <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-left">Status</th>
                    <th className="text-xs uppercase text-gray-500 tracking-widest font-semibold py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((po) => (
                    <tr key={po.id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${selected.has(po.id) ? "bg-blue-50/40" : ""}`}>
                      <td className="px-4 py-3">
                        <input type="checkbox" checked={selected.has(po.id)} onChange={() => toggleSelect(po.id)} data-testid={`select-po-${po.id}`} className="cursor-pointer" />
                      </td>
                      <td className="py-3 px-4 text-sm font-medium">
                        <Link to={`/orders/${po.id}`} data-testid={`po-row-${po.id}`} className="text-slate-900 hover:underline font-mono">
                          {po.po_number || "—"}
                        </Link>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-900">{po.customer_name || "—"}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 font-mono">{po.po_date || "—"}</td>
                      <td className="py-3 px-4 text-sm text-gray-600 font-mono">{po.due_date || "—"}</td>
                      <td className="py-3 px-4 text-sm text-right text-gray-600 font-mono">{po.line_items?.length || 0}</td>
                      <td className="py-3 px-4 text-sm text-right text-slate-900 font-mono">{po.currency || ""} {Number(po.subtotal || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusColors[po.status] || "bg-gray-50 text-gray-700 border-gray-200"}`}>
                          {po.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => removeOne(po.id, po.po_number)} data-testid={`delete-po-${po.id}`} className="text-xs text-red-600 border border-red-200 rounded-md px-2.5 py-1 hover:bg-red-50 inline-flex items-center gap-1">
                          <Trash2 size={12} /> Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
